import React from 'react';
import { Link } from 'react-router-dom';
import { FaLightbulb, FaClock, FaBullseye, FaBook, FaUsers, FaHeadphones, FaPen, FaGlobe } from 'react-icons/fa';

const LearningGuide: React.FC = () => {
  const learningStrategies = [
    {
      icon: <FaLightbulb className="text-3xl text-lavender-600" />,
      title: 'Spaced Repetition',
      description: 'Review vocabulary at increasing intervals to maximize retention. Our flashcard system automatically schedules reviews based on your performance.',
      tips: [
        'Study new words daily',
        'Review difficult words more frequently',
        'Trust the algorithm - don\'t skip reviews'
      ]
    },
    {
      icon: <FaClock className="text-3xl text-lavender-500" />,
      title: 'Consistent Practice',
      description: 'Short, daily study sessions are more effective than long, infrequent ones. Aim for 15-30 minutes every day.',
      tips: [
        'Set a daily study time',
        'Use small breaks throughout the day',
        'Make it a habit, not a chore'
      ]
    },
    {
      icon: <FaBullseye className="text-3xl text-lavender-600" />,
      title: 'Goal Setting',
      description: 'Set specific, achievable goals to stay motivated and track your progress effectively.',
      tips: [
        'Start with small, daily goals',
        'Celebrate milestones',
        'Adjust goals as you progress'
      ]
    },
    {
      icon: <FaBook className="text-3xl text-lavender-500" />,
      title: 'Contextual Learning',
      description: 'Learn words in context rather than in isolation. This helps with understanding usage and retention.',
      tips: [
        'Study complete phrases',
        'Read Persian texts',
        'Practice with real conversations'
      ]
    }
  ];

  const studyTechniques = [
    {
      title: 'Active Recall',
      description: 'Test yourself regularly instead of just re-reading. This strengthens memory pathways.',
      steps: [
        'Use flashcards actively',
        'Take quizzes regularly',
        'Try to recall words without looking'
      ]
    },
    {
      title: 'Multi-Sensory Learning',
      description: 'Engage multiple senses to create stronger memory connections.',
      steps: [
        'Listen to audio pronunciations',
        'Write words by hand',
        'Speak words out loud'
      ]
    },
    {
      title: 'Chunking',
      description: 'Group related words together to make them easier to remember.',
      steps: [
        'Learn words by themes',
        'Create word families',
        'Use mnemonic devices'
      ]
    },
    {
      title: 'Immersive Practice',
      description: 'Surround yourself with the language as much as possible.',
      steps: [
        'Change your phone language',
        'Listen to Persian music',
        'Watch Persian movies with subtitles'
      ]
    }
  ];

  const commonMistakes = [
    {
      mistake: 'Trying to learn too much at once',
      solution: 'Focus on quality over quantity. Master a few words thoroughly before moving on.',
      tip: 'Aim for 5-10 new words per day maximum.'
    },
    {
      mistake: 'Neglecting pronunciation',
      solution: 'Always listen to and practice pronunciation when learning new words.',
      tip: 'Use our audio features and repeat after native speakers.'
    },
    {
      mistake: 'Studying in isolation',
      solution: 'Learn words in context and practice using them in sentences.',
      tip: 'Use our cultural content to see words in real contexts.'
    },
    {
      mistake: 'Inconsistent practice',
      solution: 'Establish a daily routine and stick to it, even if it\'s just 10 minutes.',
      tip: 'Set reminders and make learning a daily habit.'
    },
    {
      mistake: 'Focusing only on vocabulary',
      solution: 'Balance vocabulary learning with grammar, cultural context, and conversation practice.',
      tip: 'Use our pathways to get a well-rounded education.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-lavender-50 via-lavender-100 to-lavender-200" dir="ltr">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-lavender-200 via-lavender-300 to-lavender-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-elegant text-white mb-6">
            Farsi Learning Guide
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
            Master the art of learning Farsi with proven strategies, techniques, and tips 
            from language experts and successful learners.
          </p>
        </div>
      </section>

      {/* Learning Strategies */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-elegant text-lavender-800 mb-4">
              Proven Learning Strategies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These evidence-based strategies will accelerate your Farsi learning journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {learningStrategies.map((strategy, index) => (
              <div key={index} className="bg-white rounded-2xl border border-lavender-200 p-8 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0">
                    {strategy.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-lavender-800 mb-3 text-left">
                      {strategy.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4 text-left">
                      {strategy.description}
                    </p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-left">Key Tips:</h4>
                  <ul className="space-y-2">
                    {strategy.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start space-x-3 text-gray-600 text-left">
                        <span className="text-lavender-500 mt-1">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Techniques */}
      <section className="py-16 bg-gradient-to-r from-lavender-100 to-lavender-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-elegant text-lavender-800 mb-4">
              Effective Study Techniques
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Practical techniques you can implement today to improve your learning efficiency
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {studyTechniques.map((technique, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl border border-lavender-200 shadow-sm">
                <h3 className="text-xl font-semibold text-lavender-800 mb-3 text-left">
                  {technique.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 text-left">
                  {technique.description}
                </p>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2 text-left">How to implement:</h4>
                  <ol className="space-y-2">
                    {technique.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex items-start space-x-3 text-gray-600 text-left">
                        <span className="text-lavender-600 font-bold">{stepIndex + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-elegant text-white mb-4">
              Common Learning Mistakes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Avoid these common pitfalls that can slow down your progress
            </p>
          </div>
          
          <div className="space-y-6">
            {commonMistakes.map((mistake, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl border border-lavender-200 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-red-600 mb-2 text-left">
                      ❌ {mistake.mistake}
                    </h3>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-lavender-700 mb-2 text-left">
                      ✅ Solution
                    </h4>
                    <p className="text-gray-600 text-left">
                      {mistake.solution}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-lavender-700 mb-2 text-left">
                      💡 Pro Tip
                    </h4>
                    <p className="text-gray-600 text-left">
                      {mistake.tip}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-16 bg-gradient-to-r from-lavender-200 to-lavender-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-elegant text-lavender-800 mb-4">
              Your Learning Path
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to mastering Farsi from beginner to advanced
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-lavender-200 text-center shadow-sm">
              <div className="text-4xl mb-4">🌱</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2 text-center">Beginner</h3>
              <p className="text-black text-sm mb-4 text-center">
                Start with basic vocabulary and simple phrases
              </p>
              <ul className="text-left text-sm space-y-1 text-black">
                <li>• Learn alphabet and sounds</li>
                <li>• Basic greetings</li>
                <li>• Numbers and colors</li>
                <li>• Simple sentences</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-lavender-200 text-center shadow-sm">
              <div className="text-4xl mb-4">🌿</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2 text-center">Elementary</h3>
              <p className="text-black text-sm mb-4 text-center">
                Build vocabulary and basic grammar skills
              </p>
              <ul className="text-left text-sm space-y-1 text-black">
                <li>• Expand vocabulary</li>
                <li>• Basic grammar rules</li>
                <li>• Simple conversations</li>
                <li>• Cultural basics</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-lavender-200 text-center shadow-sm">
              <div className="text-4xl mb-4">🌳</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2 text-center">Intermediate</h3>
              <p className="text-black text-sm mb-4 text-center">
                Develop fluency and cultural understanding
              </p>
              <ul className="text-left text-sm space-y-1 text-black">
                <li>• Complex grammar</li>
                <li>• Cultural immersion</li>
                <li>• Reading comprehension</li>
                <li>• Poetry and literature</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-lavender-200 text-center shadow-sm">
              <div className="text-4xl mb-4">🏔️</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2 text-center">Advanced</h3>
              <p className="text-black text-sm mb-4 text-center">
                Master the language and cultural nuances
              </p>
              <ul className="text-left text-sm space-y-1 text-black">
                <li>• Literary analysis</li>
                <li>• Academic writing</li>
                <li>• Cultural expertise</li>
                <li>• Teaching others</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-elegant text-lavender-800 mb-4">
              Additional Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore these resources to enhance your learning experience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/flashcards"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">🃏</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Flashcards</h3>
              <p className="text-gray-600 text-sm text-center">
                Master vocabulary with our intelligent flashcard system
              </p>
            </Link>
            
            <Link
              to="/quiz"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">❓</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Quizzes</h3>
              <p className="text-gray-600 text-sm text-center">
                Test your knowledge with adaptive quizzes
              </p>
            </Link>
            
            <Link
              to="/pathways"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">🛤️</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Pathways</h3>
              <p className="text-gray-600 text-sm text-center">
                Follow structured learning journeys
              </p>
            </Link>
            
            <Link
              to="/cultural"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">🏛️</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Cultural Content</h3>
              <p className="text-gray-600 text-sm text-center">
                Immerse yourself in Persian culture
              </p>
            </Link>
            
            <Link
              to="/forum"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">👥</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Community</h3>
              <p className="text-gray-600 text-sm text-center">
                Connect with fellow learners
              </p>
            </Link>
            
            <Link
              to="/help"
              className="bg-white p-6 rounded-2xl border border-lavender-200 hover:shadow-lg hover:shadow-lavender-200/50 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">❓</div>
              <h3 className="text-lg font-semibold text-lavender-800 mb-2">Help Center</h3>
              <p className="text-gray-600 text-sm text-center">
                Get help and support when needed
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-lavender-300 to-lavender-400">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-elegant text-lavender-800 mb-6">
            Ready to Apply These Strategies?
          </h2>
          <p className="text-xl text-gray-700 mb-8 text-center">
            Start implementing these techniques today and accelerate your Farsi learning journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pathways/kitchen-farsi"
              className="bg-lavender-600 hover:bg-lavender-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start Learning
            </Link>
            <Link
              to="/pathways"
              className="px-8 py-4 border-2 border-lavender-600 text-lavender-600 font-semibold rounded-2xl hover:bg-lavender-50 transition-all duration-300"
            >
              Explore Pathways
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LearningGuide; 